import { useEffect, useMemo, useState } from "react";
import api from "../../services/api";

function asUsersArray(data) {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.users)) return data.users;
  return [];
}

function asId(value) {
  if (!value) return "";
  if (typeof value === "string") return value;
  if (typeof value === "object") return String(value._id || value.id || "");
  return String(value);
}

function getAssignedTechnicianId(workorder) {
  const direct = asId(workorder?.assignedTo);
  if (direct) return direct;

  const list = Array.isArray(workorder?.assignedUsers) ? workorder.assignedUsers : [];
  for (const entry of list) {
    const id = asId(entry);
    if (id) return id;
  }
  return "";
}

function toDateInput(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeHourEntry(entry = {}) {
  return {
    datum: entry.datum,
    hourTypeId: entry.hourTypeId || undefined,
    hourTypeName: entry.hourTypeName || "",
    description: entry.description || "",
    technicianId: entry.technicianId || undefined,
    hours: Number(entry.hours || 0),
    sellRate:
      entry.sellRate === null || entry.sellRate === undefined || Number(entry.sellRate) <= 0
        ? undefined
        : Number(entry.sellRate),
    costRate:
      entry.costRate === null || entry.costRate === undefined || Number(entry.costRate) <= 0
        ? undefined
        : Number(entry.costRate),
    vatRate: entry.vatRate ?? undefined,
    billable: entry.billable !== false,
  };
}

export default function UurToevoegenModal({ werkbonId, onClose, onSave }) {
  const [form, setForm] = useState({
    technicianId: "",
    datum: "",
    startTijd: "",
    eindTijd: "",
    pauzeMinuten: 0,
    hours: "",
    hourTypeId: "",
    omschrijving: "",
  });

  const [hourTypes, setHourTypes] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [existingHourEntries, setExistingHourEntries] = useState([]);
  const [selectedEntryIndex, setSelectedEntryIndex] = useState("-1");

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function loadOptions() {
      const [typesResult, usersResult, werkbonResult, financialResult] = await Promise.allSettled([
        api.get("/tenant-hour-types/options"),
        api.get("/users/technicians/options"),
        api.get(`/werkbonnen/${werkbonId}`),
        api.get(`/werkbonnen/${werkbonId}/financial`),
      ]);

      if (cancelled) return;

      const nextHourTypes =
        typesResult.status === "fulfilled" && Array.isArray(typesResult.value?.data)
          ? typesResult.value.data
          : [];

      const nextTechnicians =
        usersResult.status === "fulfilled" ? asUsersArray(usersResult.value?.data) : [];

      const workorder =
        werkbonResult.status === "fulfilled" ? werkbonResult.value?.data || {} : {};

      const nextExistingEntries =
        financialResult.status === "fulfilled" && Array.isArray(financialResult.value?.data?.hourEntries)
          ? financialResult.value.data.hourEntries
          : [];

      const assignedTechnicianId = getAssignedTechnicianId(workorder);

      const fallbackAssignedTechnician =
        assignedTechnicianId && workorder?.assignedTo && typeof workorder.assignedTo === "object"
          ? [
              {
                _id: assignedTechnicianId,
                naam: workorder.assignedTo?.naam || "",
                email: workorder.assignedTo?.email || "",
              },
            ]
          : [];

      const mergedTechnicians = nextTechnicians.length > 0 ? nextTechnicians : fallbackAssignedTechnician;

      setHourTypes(nextHourTypes);
      setTechnicians(mergedTechnicians);
      setExistingHourEntries(nextExistingEntries);

      const preferredTechnicianId =
        mergedTechnicians.some((item) => String(item?._id || "") === String(assignedTechnicianId || ""))
          ? String(assignedTechnicianId)
          : String(mergedTechnicians[0]?._id || "");

      setForm((prev) => ({
        ...prev,
        technicianId: prev.technicianId || preferredTechnicianId,
        hourTypeId: prev.hourTypeId || String(nextHourTypes[0]?._id || ""),
      }));

      if (nextHourTypes.length === 0) {
        setError("Geen uursoorten beschikbaar of geen rechten op uursoorten.");
      } else if (mergedTechnicians.length === 0) {
        setError("Geen monteurs beschikbaar of geen rechten op monteurselectie.");
      }
    }

    loadOptions();

    return () => {
      cancelled = true;
    };
  }, [werkbonId]);

  const selectedHourType = useMemo(
    () => hourTypes.find((item) => String(item._id) === String(form.hourTypeId)),
    [hourTypes, form.hourTypeId]
  );

  const handleChange = (e) => {
    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleEntrySelect = (e) => {
    const value = String(e.target.value || "-1");
    setSelectedEntryIndex(value);

    if (value === "-1") {
      setForm((prev) => ({
        ...prev,
        datum: "",
        startTijd: "",
        eindTijd: "",
        pauzeMinuten: 0,
        hours: "",
        omschrijving: "",
      }));
      return;
    }

    const index = Number(value);
    const entry = existingHourEntries[index];
    if (!entry) return;

    setForm((prev) => ({
      ...prev,
      technicianId: String(entry.technicianId || prev.technicianId || ""),
      datum: toDateInput(entry.datum),
      startTijd: "",
      eindTijd: "",
      pauzeMinuten: 0,
      hours: String(Number(entry.hours || 0)),
      hourTypeId: String(entry.hourTypeId || prev.hourTypeId || ""),
      omschrijving: String(entry.description || ""),
    }));
  };

  function calcHours() {
    const manualHours = Number(form.hours || 0);
    if (Number.isFinite(manualHours) && manualHours > 0) {
      return manualHours;
    }

    if (!form.startTijd || !form.eindTijd) return NaN;
    const start = new Date(`1970-01-01T${form.startTijd}:00`);
    const end = new Date(`1970-01-01T${form.eindTijd}:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return NaN;
    const diffHours = (end.getTime() - start.getTime()) / 3600000;
    const pauseHours = Number(form.pauzeMinuten || 0) / 60;
    return Math.max(0, diffHours - pauseHours);
  }

  const handleSubmit = async () => {
    setError("");
    setLoading(true);

    try {
      const hours = calcHours();
      if (!Number.isFinite(hours) || hours <= 0) {
        throw new Error("Vul een geldig aantal uren in of controleer start/eindtijd en pauze.");
      }

      if (!String(form.technicianId || "").trim()) {
        throw new Error("Kies een monteur.");
      }

      if (!String(form.hourTypeId || "").trim()) {
        throw new Error("Kies een uursoort.");
      }

      const finRes = await api.get(`/werkbonnen/${werkbonId}/financial`);
      const existing = Array.isArray(finRes?.data?.hourEntries) ? finRes.data.hourEntries : [];

      const selectedTechnicianId = String(form.technicianId || "").trim();
      const selectedHourTypeId = String(form.hourTypeId || "").trim();
      const selectedHourTypeName = String(selectedHourType?.name || "").trim();

      const normalizedExisting = existing.map((entry) =>
        normalizeHourEntry({
          ...entry,
          hourTypeId: entry.hourTypeId || selectedHourTypeId,
          hourTypeName: entry.hourTypeName || selectedHourTypeName,
          technicianId: entry.technicianId || selectedTechnicianId,
        })
      );

      const nextEntry = {
        datum: form.datum ? new Date(form.datum).toISOString() : new Date().toISOString(),
        hourTypeId: selectedHourTypeId,
        hourTypeName: selectedHourTypeName,
        description: form.omschrijving || selectedHourTypeName || "Uurregel",
        technicianId: selectedTechnicianId,
        hours,
      };

      const index = Number(selectedEntryIndex);
      if (Number.isInteger(index) && index >= 0 && index < normalizedExisting.length) {
        normalizedExisting[index] = {
          ...normalizedExisting[index],
          ...nextEntry,
        };
      } else {
        normalizedExisting.push(nextEntry);
      }

      await api.put(`/werkbonnen/${werkbonId}/hour-entries`, { hourEntries: normalizedExisting });
      onSave?.();
      onClose();
    } catch (err) {
      console.error(err);
      setError(err?.response?.data?.message || err?.message || "Er ging iets mis bij opslaan van de uren.");
    }

    setLoading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="w-96 rounded-sm border border-gray-300 bg-white p-6 shadow-xl">
        <h2 className="mb-4 text-xl font-semibold text-blue-700">Uren toevoegen / aanpassen</h2>

        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium">Regel</label>
            <select
              value={selectedEntryIndex}
              onChange={handleEntrySelect}
              className="w-full border border-gray-300 p-2"
            >
              <option value="-1">Nieuwe uurregel toevoegen</option>
              {existingHourEntries.map((entry, idx) => (
                <option key={`entry-${idx}`} value={idx}>
                  {`${toDateInput(entry.datum) || "onbekend"} - ${Number(entry.hours || 0).toFixed(2)} uur (${entry.hourTypeName || "uur"})`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium">Monteur</label>
            <select
              name="technicianId"
              value={form.technicianId}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2"
            >
              {technicians.length === 0 && <option value="">Geen monteurs gevonden</option>}
              {technicians.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.naam || item.name || item.email || "Monteur"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium">Datum</label>
            <input
              type="date"
              name="datum"
              value={form.datum}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Aantal uur</label>
            <input
              type="number"
              name="hours"
              min="0"
              step="0.25"
              value={form.hours}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2"
              placeholder="Bijv. 9"
            />
            <p className="mt-1 text-xs text-gray-500">Laat leeg om start/eindtijd te gebruiken.</p>
          </div>

          <div className="flex gap-3">
            <div className="flex-1">
              <label className="block text-sm font-medium">Starttijd</label>
              <input
                type="time"
                name="startTijd"
                value={form.startTijd}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2"
              />
            </div>

            <div className="flex-1">
              <label className="block text-sm font-medium">Eindtijd</label>
              <input
                type="time"
                name="eindTijd"
                value={form.eindTijd}
                onChange={handleChange}
                className="w-full border border-gray-300 p-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium">Pauze (minuten)</label>
            <input
              type="number"
              name="pauzeMinuten"
              value={form.pauzeMinuten}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium">Uursoort</label>
            <select
              name="hourTypeId"
              value={form.hourTypeId}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2"
            >
              {hourTypes.length === 0 && <option value="">Geen uursoorten gevonden</option>}
              {hourTypes.map((item) => (
                <option key={item._id} value={item._id}>
                  {item.name} (€ {Number(item.sellRate || 0).toFixed(2)})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium">Omschrijving</label>
            <textarea
              name="omschrijving"
              value={form.omschrijving}
              onChange={handleChange}
              className="h-20 w-full border border-gray-300 p-2"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="mt-5 flex justify-end gap-3">
          <button onClick={onClose} className="border border-gray-500 bg-gray-300 px-4 py-2">
            Annuleren
          </button>

          <button
            onClick={handleSubmit}
            className="border border-blue-800 bg-blue-600 px-4 py-2 text-white"
            disabled={loading}
          >
            {loading ? "Opslaan..." : "Opslaan"}
          </button>
        </div>
      </div>
    </div>
  );
}
